'use client';

import { getJobDataListFromFilter, JobData } from "app/browser/[id]/ServerActions";
import { Tooltip } from 'flowbite-react';
import { useAtomValue } from "jotai";
import { formatDate } from "lib/clientsafe-utils";
import { isNumber } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { FaBrush, FaCalendar, FaCamera, FaClock, FaCopy, FaEye, FaImage, FaKey, FaSourcetree } from "react-icons/fa6";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import JobCard from "./JobCard";
import PageNav from "./PageNav";
import { StyledLink } from "./StyledLink";
import StyledSpinner from "./StyledSpinner";
import { jobListLayoutAtom } from "./atoms/jobListAtoms";
import { JobListFilter } from "./FilterableListbox-utils";
import {log} from '@logtail/next';

type JobListProps = {
    filter: JobListFilter,
    showStatus?: boolean,
    showArtist?: boolean,
    showTitle?: boolean,
    showCreateDate?: boolean,
    featuredOnly?: boolean,
    useMasonryLayout?: boolean,
    page?: number,
}

const PAGE_SIZE = 25;

export default function JobList({
    filter,
    showStatus = true,
    showArtist = true,
    showTitle = true,
    showCreateDate = true,
    useMasonryLayout = false,
    page = 1,
}: JobListProps) {

    const [animationsToDisplay, setAnimationsToDisplay] = useState<JobData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalJobs, setTotalJobs] = useState<number | undefined>();
    const layout = useAtomValue(jobListLayoutAtom);
    
    const displayMode = useMasonryLayout ? 'masonry' : layout;
    const paginatedFilter = useMemo(() => ({
        ...filter,
        skip: (page - 1) * PAGE_SIZE, // pageNumber is 1-indexed
        take: PAGE_SIZE,
    }), [filter, page]);

    useEffect(() => {
        setLoading(true);
        getJobDataListFromFilter(paginatedFilter).then((result) => {
            setTotalJobs(result.totalJobs);
            setAnimationsToDisplay(result.jobData);
        }).catch((error : unknown) => {
            log.error("Browser error: failed to retrieve jobs", {error, paginatedFilter});
        }).finally(() => {
            setLoading(false);
        });
    }, [paginatedFilter]);



    if (loading) {
        return <><StyledSpinner /> Loading...</>;
    }

    return <>
        <PageNav
            key={"topnav"}
            currentPage={page}
            totalPages={isNumber(totalJobs) ? Math.ceil(totalJobs / PAGE_SIZE) : 0}
        />

        {displayMode === 'masonry' && <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: 4 }} >
            <Masonry gutter="0.5rem">
                {animationsToDisplay.map((animation, idx) => (
                    animation.displayData && <JobCard
                        liveUpdate={true}
                        useAspectRatio={true}
                        key={`job-${idx}`}
                        displayData={animation.displayData}
                        adminOnlyData={animation.adminOnlyData}
                        showStatus={false}
                        showArtist={true}
                        showTitle={true}
                        showCreateDate={true}
                    />
                ))}
            </Masonry>
        </ResponsiveMasonry>}

        {displayMode === 'wall' && <div className="flex flex-col md:flex-row flex-wrap justify-center items-center gap-8">
            {animationsToDisplay.map((animation, idx) =>
                animation.displayData && <JobCard
                    liveUpdate={true}                
                    key={`job-${idx}`}
                    displayData={animation.displayData}
                    adminOnlyData={animation.adminOnlyData}
                    showStatus={showStatus}
                    showArtist={showArtist}
                    showTitle={showTitle}
                    showCreateDate={showCreateDate} />)}
        </div>}

        {displayMode === 'list' && <div className="flex flex-col gap-4 max-w-7xl mx-auto">
            {animationsToDisplay.map((animation, idx) => {
                return (animation.displayData && animation.formData && <div key={`job-${idx}`} className="flex md:flex-row xs:flex-col">
                    <div>
                        <JobCard
                            liveUpdate={true}
                            key={`job-${idx}`}
                            displayData={animation.displayData}
                            adminOnlyData={animation.adminOnlyData}
                            showStatus={showStatus}
                            showArtist={false}
                            showTitle={false}
                            showCreateDate={false} />
                    </div>
                    {getAnimationDetails(idx, animation)}
                </div>)
            })
            }
        </div>}

        <PageNav
            key={"bottomnav"} 
            currentPage={page}
            totalPages={isNumber(totalJobs) ? Math.ceil(totalJobs / PAGE_SIZE) : 0}/>
    </>

    function getAnimationDetails(idx: number, animation: JobData) {
        if (!animation.formData || !animation.displayData) {
            return <></>;
        }

        return <div key={`job-details-${idx}`} className="border border-black/50 dark:border-white/50 md:border-l-0 flex-1 flex flex-col p-4">
            <h4 className='text-2xl font-bold'>{animation.formData.prompt}</h4>
            <span>by {animation.displayData.artistName}</span>

            <hr className="my-4 dark:border-white/25  border-black/25" />

            <div className="grid grid-cols-3 gap-4 dark:text-white/50 text-black/50">
                <div className="flex flex-row items-center gap-2">
                    <FaKey />
                    <Tooltip content="ID">
                        <span>{animation.displayData.displayName}</span>
                    </Tooltip>
                </div>

                <div className="flex flex-row items-center gap-2">
                    <FaCalendar />
                    <Tooltip content={formatDate(animation.displayData.createdAt)}>
                        <span>{DateTime.fromJSDate(new Date(animation.displayData.createdAt)).toRelative()}</span>
                    </Tooltip>
                </div>

                <div className="flex flex-row items-center gap-2">
                    <FaBrush />
                    <Tooltip content="Style">
                        <span>{animation.formData.style}</span>
                    </Tooltip>
                </div>

                <div className="flex flex-row items-center gap-2">
                    <FaCamera />
                    <Tooltip content="Motion preset">
                        <span>{animation.displayData.isAudioReactive ? animation.formData.audioSyncMotionPreset : animation.formData.classicMotionPreset}</span>
                    </Tooltip>
                </div>

                {(animation.displayData.basedOn) && <div className="flex flex-row items-center gap-2">
                    <FaCopy />
                    <StyledLink discrete href={`/animate/${animation.displayData.basedOn}`}>forked from this</StyledLink>
                </div>}

                {animation.adminOnlyData && <div className="flex flex-row items-center gap-2">
                    <FaImage />
                    <Tooltip content="Image prompt">
                        <div className='text'>str={animation.formData.imagePromptWeight} ; nsfw={animation.formData.imagePromptNsfwScore}</div>
                    </Tooltip>
                </div>}

                {animation.adminOnlyData && <div className="flex flex-row items-center gap-2">
                    <FaSourcetree />
                    <Tooltip content="Source">
                        <span>{animation.displayData.source}</span>
                    </Tooltip>
                </div>}

                {animation.adminOnlyData && <div className="flex flex-row items-center gap-2">
                    <FaEye />
                    <Tooltip content="Visibility">
                        <span>anim={animation.adminOnlyData.isPublic ? 'public' : 'private'} ; prompt={animation.adminOnlyData.hasPublicPrompt ? 'public' : 'private'}</span>
                    </Tooltip>
                </div>}

                {animation.adminOnlyData && <div className="flex flex-row items-center gap-2">
                    <FaEye />
                    <Tooltip content="Backend & Farm">
                        <span>{animation.adminOnlyData.gpuFarm} / {animation.adminOnlyData.backend}</span>
                    </Tooltip>
                </div>}

                {animation.adminOnlyData && <div className="flex flex-row items-center gap-2">
                    <FaClock />
                    <Tooltip content="Gen time">
                        <span>gen={animation.adminOnlyData.genTime?.toFixed(0)}s ; queue={animation.adminOnlyData.waitTime?.toFixed(0)}s</span>
                    </Tooltip>
                </div>}
            </div>
        </div>;
    }
}
